<script setup lang="ts">
/** Section « Devis liés » — rattachement (à tout moment) des devis du client. */
import { computed, ref } from 'vue'
import { useToast } from 'vue-toast-notification'
import { BanknotesIcon, PlusIcon, TrashIcon, TrophyIcon, ClockIcon } from '@heroicons/vue/24/outline'
import type { AccompRequest } from '@/v2/accompagnement/api/accompagnementTypes'
import { canManageQuotes } from '@/v2/accompagnement/helpers/accompagnementPermissions'
import { formatEuros } from '@/v2/accompagnement/helpers/accompagnementFormat'
import { useAccompagnementStore } from '@/v2/accompagnement/store/useAccompagnementStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import AccompQuotePickerDialog from '@/v2/accompagnement/components/AccompQuotePickerDialog.vue'

const props = defineProps<{ request: AccompRequest }>()
const store = useAccompagnementStore()
const perms = useBoostCorePermissionStore()
const toast = useToast()

const pickerOpen = ref(false)
const detaching = ref<string | null>(null)
const canManage = computed(() => canManageQuotes(props.request, perms, store.currentUserUuid))

/** CA / marge des devis gagnés (sale/done) — synthèse de la demande. */
const wonCa = computed(() => props.request.quotes.filter((q) => q.won).reduce((s, q) => s + q.totalCa, 0))
const wonMarge = computed(() => props.request.quotes.filter((q) => q.won).reduce((s, q) => s + q.marge, 0))

async function detach(linkId: string) {
  detaching.value = linkId
  const res = await store.detachQuote(props.request.id, linkId)
  detaching.value = null
  if (res) toast.success('Devis détaché.')
  else toast.error(store.actionError ?? 'Échec du détachement.')
}
</script>

<template>
  <section class="card border border-base-300 bg-base-100 shadow-sm">
    <div class="card-body gap-4 p-4 md:p-6">
      <div class="flex items-center justify-between gap-2">
        <h2 class="card-title flex items-center gap-2 text-lg font-semibold">
          <BanknotesIcon class="h-5 w-5 text-primary" aria-hidden="true" />
          Devis liés
        </h2>
        <button v-if="canManage" type="button" class="btn btn-outline btn-sm gap-1" @click="pickerOpen = true">
          <PlusIcon class="h-4 w-4" aria-hidden="true" />
          Rattacher un devis
        </button>
      </div>

      <p v-if="wonCa > 0" class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-base-content/80">
        <span>CA gagné : <span class="font-semibold tabular-nums">{{ formatEuros(wonCa) }}</span></span>
        <span>Marge : <span class="font-semibold tabular-nums">{{ formatEuros(wonMarge) }}</span></span>
      </p>

      <p v-if="!request.quotes.length" class="text-base-content/70">
        Aucun devis rattaché.
        <span v-if="canManage">Rattachez-en un pour pouvoir clôturer en « Gagnée ».</span>
      </p>

      <ul v-else role="list" class="space-y-2">
        <li
          v-for="q in request.quotes"
          :key="q.id"
          class="flex items-center justify-between gap-3 rounded-lg border border-base-200 bg-base-200/40 p-3"
        >
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-medium tabular-nums text-base-content">{{ q.quoteReference }}</span>
              <span class="badge badge-sm gap-1" :class="q.won ? 'badge-success' : 'badge-ghost'">
                <component :is="q.won ? TrophyIcon : ClockIcon" class="h-3 w-3" aria-hidden="true" />
                {{ q.won ? 'Gagné' : 'En cours' }}
              </span>
            </div>
            <p class="mt-0.5 text-sm text-base-content/70">
              <span class="tabular-nums">{{ formatEuros(q.totalCa) }}</span> HT ·
              marge <span class="tabular-nums">{{ formatEuros(q.marge) }}</span>
              <span class="opacity-70"> · par {{ q.attachedByName }}</span>
            </p>
          </div>
          <button
            v-if="canManage"
            type="button"
            class="btn btn-ghost btn-sm gap-1 text-error"
            :disabled="detaching === q.id"
            @click="detach(q.id)"
          >
            <TrashIcon class="h-4 w-4" aria-hidden="true" />
            <span class="sr-only sm:not-sr-only">Détacher</span>
          </button>
        </li>
      </ul>

      <AccompQuotePickerDialog :open="pickerOpen" :request-id="request.id" @close="pickerOpen = false" />
    </div>
  </section>
</template>
